import React, { FormEvent, Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSnackbar } from 'notistack';

import Add from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import Spinner from '~/components/Common/Spinner';

import {
  AVAILABLE_REGIONS_LIB_REGIONS,
  COMMISSION_RULE_CONDITION_TYPES,
  CommissionOption,
  CommissionsConditionTypes,
  INITIAL_COMMISSION_CONDITIONS,
  PRODUCT_TYPES_OPTIONS,
  RuleCondition,
  RuleConditions,
} from '~/consts/agentHub';

import { useCreateCommission } from '~/hooks/apis/agent/useCreateCommission';
import { useGetAgenciesById } from '~/hooks/apis/agent/useGetAgenciesById';
import { useGetUsersSummaryByIds } from '~/hooks/apis/agent/useGetUsersSummaryByIds';
import { useUpdateCommission } from '~/hooks/apis/agent/useUpdateCommission';
import useCurrentBrand from '~/hooks/useCurrentBrand';
import useUserBreadcrumbs from '~/hooks/useUserBreadcrumbs';

import { Dayjs, formatDateLongISO, formatDateSlashes, toDayJs } from '~/services/TimeService';

import type { Commission, CreateCommissionParams, EditCommissionParams } from '~/types/services/agentHub';

import { arrayToObject } from '~/utils/arrayUtils';
import { buildRuleParam, groupByValue, mergeCommissionAdditionalInfo } from '~/utils/commission';
import {
  normalizeApiRulesForCommissionForm,
  overlayFlightLocationsFromAdditionalInfo,
  readCommissionAdditionalInfo,
} from '~/utils/commissionFlightLocations';
import { capitalise } from '~/utils/stringUtils';

import { AddNewConditionForm } from './AddNewConditionForm';

interface Props {
  data?: Commission;
  isOpen: boolean;
  onClose: () => void;
  mode: 'create' | 'edit';
}

type InputOptionData = string | number | Record<string, unknown>;
type InputOptionFunc = (data: InputOptionData) => { value: string; label: string };

type CommissionType = 'base' | 'additional' | 'incentive' | '';
const VALID_COMMISSION_TYPES = new Set<CommissionType>(['base', 'additional', 'incentive']);

const getCommissionLimitInputName = (region: string) => `commissionLimit-${region}`;

const SHRUNK_LABEL = {
  textField: {
    InputLabelProps: { shrink: true },
  },
};

const INPUT_BORDER = {
  sx: {
    borderRadius: 0.5,
  },
};

export default function CommissionDialog({ data, onClose, isOpen, mode }: Props) {
  const isCreate = mode === 'create';
  const isEdit = mode === 'edit';
  const textTransformNone = { textTransform: 'none' };

  const [createdConditions, setCreatedConditions] = useState<RuleConditions>(INITIAL_COMMISSION_CONDITIONS);
  const [regions, setRegions] = useState<Array<string>>([]);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const commissionFormRef = useRef<HTMLFormElement>(null);
  const [visibleConditions, setVisibleConditions] = useState<Set<CommissionsConditionTypes>>(new Set());
  const [shouldIncludeLimit, setShouldIncludeLimit] = useState<boolean>(false);
  const [commissionTypeChoice, setCommissionTypeChoice] = useState<CommissionType>('');
  const [priorityInput, setPriorityInput] = useState<string>('');
  const [canHaveAdditionalCommissions, setCanHaveAdditionalCommissions] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  const brand = useCurrentBrand();

  const createCommissionMutation = useCreateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule created', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });
  const updateCommissionMutation = useUpdateCommission({
    onSuccess: () => {
      enqueueSnackbar('Commission rule updated', { variant: 'success' });
    },
    onError: (error) => {
      enqueueSnackbar(error?.message || 'Unknown error', { variant: 'error' });
    },
  });

  const groupedProductedTypes = useMemo(() => {
    return arrayToObject(PRODUCT_TYPES_OPTIONS, (productType) => productType);
  }, []);
  const totalConditions = useMemo(() => visibleConditions.size, [visibleConditions]);
  const remainingConditions = COMMISSION_RULE_CONDITION_TYPES.filter((cond) => !visibleConditions.has(cond));
  const getRuleTypeFunction = useCallback(
    (type: string): InputOptionFunc => {
      switch (type) {
        case 'productType':
          return (data) => {
            const key = String(data);
            return { value: key, label: groupedProductedTypes[key] ?? key };
          };
        case 'flightCarrier':
          return (data) => {
            if (data != null && typeof data === 'object' && !Array.isArray(data)) {
              const o = data as { code?: string; name?: string; airlineCode?: string; airlineName?: string };
              const code = o.code ?? o.airlineCode ?? '';
              const name = o.name ?? o.airlineName ?? code;
              return { value: code, label: name };
            }
            const code = String(data);
            return { value: code, label: code };
          };
        default:
          return (data) => {
            if (typeof data === 'object' && data != null && !Array.isArray(data)) {
              const firstPrimitive = Object.values(data).find(
                (value): value is string | number => typeof value === 'string' || typeof value === 'number',
              );
              const normalized = firstPrimitive != null ? String(firstPrimitive) : '';
              return { value: normalized, label: normalized };
            }
            const normalized = String(data);
            return { value: normalized, label: normalized };
          };
      }
    },
    [groupedProductedTypes],
  );

  const agencyIds = useMemo(() => {
    if (!data?.rules?.agency || !isOpen) {
      return [];
    }
    return data.rules.agency;
  }, [data?.rules?.agency, isOpen]);

  const agentIds = useMemo(() => {
    if (!data?.rules?.agent || !isOpen) {
      return [];
    }
    return data.rules.agent;
  }, [data?.rules?.agent, isOpen]);

  const { data: agenciesData, isFetching: isLoadingAgencies } = useGetAgenciesById(agencyIds);
  const { data: agentsMap, isFetching: isLoadingAgents } = useGetUsersSummaryByIds(agentIds, brand);
  const { data: creator, isLoading: isLoadingCreator } = useUserBreadcrumbs({
    userId: isEdit && data?.createdBy,
    enabled: Boolean(isEdit && data?.createdBy),
  });
  const { data: updater, isLoading: isLoadingUpdater } = useUserBreadcrumbs({
    userId: isEdit && data?.updatedBy,
    enabled: Boolean(isEdit && data?.updatedBy),
  });

  const isLoadingCommissionEditHistory = isLoadingCreator || isLoadingUpdater;
  const isLoadingConditions = isLoadingAgencies || isLoadingAgents;

  const resetForm = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    setRegions([]);
    setCreatedConditions(INITIAL_COMMISSION_CONDITIONS);
    setVisibleConditions(new Set());
    setShouldIncludeLimit(false);
    setCommissionTypeChoice('');
    setPriorityInput('');
    setCanHaveAdditionalCommissions(true);
    commissionFormRef.current?.reset();
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (isCreate && !data) {
      setCommissionTypeChoice('');
      setPriorityInput('');
      setCanHaveAdditionalCommissions(true);
    }
  }, [isOpen, isCreate, data]);

  useEffect(() => {
    if (!data || !isOpen) {
      return;
    }
    setRegions(data.regions);
    setCommissionTypeChoice(VALID_COMMISSION_TYPES.has(data.type as CommissionType) ? data.type : '');
    setPriorityInput(data.priority != null ? String(data.priority) : '');
    setCanHaveAdditionalCommissions(Boolean(data.canHaveAdditionalCommissions));
    if (data.startDate) {
      setStartDate(toDayJs(data.startDate));
    }

    if (data.endDate) {
      setEndDate(toDayJs(data.endDate));
    } else {
      setEndDate(null);
    }
    setShouldIncludeLimit(data.limit != null);

    const rawRules = normalizeApiRulesForCommissionForm({ ...(data.rules as Record<string, unknown>) });

    let conditionsObject: RuleConditions = {
      ...INITIAL_COMMISSION_CONDITIONS,
      ...arrayToObject(
        Object.entries(rawRules),
        ([key]) => key as keyof RuleConditions,
        ([key, rules]) => (rules as Array<InputOptionData>).map((rule) => getRuleTypeFunction(key)(rule)),
      ),
    };

    conditionsObject = overlayFlightLocationsFromAdditionalInfo(conditionsObject, readCommissionAdditionalInfo(data));

    if (agenciesData && agencyIds.length > 0) {
      conditionsObject.agency = agenciesData.map((agency) => ({ value: agency.id, label: agency.name }));
    }

    if (agentsMap && agentIds.length > 0) {
      const agentsArray = Array.from(agentsMap.values());
      conditionsObject.agent = agentsArray.map((agent: { id_member: string; full_name: string; email: string }) => ({
        value: agent.id_member,
        label: `${agent.full_name} [${agent.email}]`,
      }));
    }

    setCreatedConditions(conditionsObject);
    const visibleKeys = Object.keys(conditionsObject).filter(
      (k) => (conditionsObject[k as keyof RuleConditions]?.length ?? 0) > 0,
    ) as Array<CommissionsConditionTypes>;
    setVisibleConditions(new Set(visibleKeys));

    return () => {
      resetForm();
    };
  }, [data, isOpen, getRuleTypeFunction, agenciesData, agentsMap, agencyIds.length, agentIds.length, resetForm]);

  const addCondition = useCallback((newCondition: RuleCondition, replace = false) => {
    setCreatedConditions((createdConditions) => {
      const conditions = createdConditions[newCondition.type];
      if (!conditions) {
        return { ...createdConditions, [newCondition.type]: [...newCondition.value] };
      }
      const currentConditions = replace ? [] : conditions.reduce(groupByValue, new Map());
      const newConditions = newCondition.value.reduce(groupByValue, new Map());
      const mergedConditions = Array.from(new Map([...currentConditions, ...newConditions]).entries());
      const newConditionsForType = mergedConditions.map(([value, label]) => ({ value, label }));
      return {
        ...createdConditions,
        [newCondition.type]: newConditionsForType,
      };
    });
  }, []);

  const handleFlightCarrierChange = useCallback(
    (next: CommissionOption) => {
      addCondition({ type: 'flightCarrier', value: next }, true);
    },
    [addCondition],
  );

  function handleChangeCondition(prevType: CommissionsConditionTypes, type: CommissionsConditionTypes) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
        [prevType]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.delete(prevType);
      setConditions.add(type);
      return new Set(setConditions);
    });
  }

  function handleClearCondition(type: string) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.delete(type as CommissionsConditionTypes);
      return new Set(setConditions);
    });
  }

  function handleAppendCondition(type: CommissionsConditionTypes) {
    setCreatedConditions((createdConditions) => {
      return {
        ...createdConditions,
        [type]: [],
      };
    });
    setVisibleConditions((setConditions) => {
      setConditions.add(type);
      return new Set(setConditions);
    });
  }

  function handleStartDateChange(date: Dayjs) {
    setStartDate(date);
  }

  function handleEndDateChange(date: Dayjs) {
    setEndDate(date);
  }

  async function handleSaveCommission(event: FormEvent<HTMLFormElement>, id: string, conditions: RuleConditions) {
    event.preventDefault();

    const isConditionsEmpty = Object.values(createdConditions).every((condition) => condition.length === 0);

    if (isConditionsEmpty) {
      enqueueSnackbar('Please add at least one condition', { variant: 'error' });
      return;
    }

    const formData = new FormData(event.currentTarget);
    const regionsForm = formData?.get('regions') as string;
    const regionsValue = regionsForm === '' ? [] : regionsForm.split(',');
    const limitValue = formData.get('limit') as string;

    if (!VALID_COMMISSION_TYPES.has(commissionTypeChoice)) {
      enqueueSnackbar('Please select a commission type', { variant: 'error' });
      return;
    }

    let priority: number | null = null;
    if (commissionTypeChoice === 'base') {
      const trimmedPriority = priorityInput.trim();
      if (trimmedPriority !== '') {
        const parsed = parseInt(trimmedPriority, 10);
        if (Number.isNaN(parsed) || parsed < 0) {
          enqueueSnackbar('Priority must be a number greater or equal to 0', { variant: 'error' });
          return;
        }
        priority = parsed;
      }
    }

    const commissionValueLimits: Array<{ region: string; maxAmount: number }> = [];
    if (commissionTypeChoice === 'incentive') {
      for (const region of regionsValue) {
        const maxAmountValue = formData.get(getCommissionLimitInputName(region)) as string;
        const trimmedMaxAmountValue = maxAmountValue.trim();

        if (trimmedMaxAmountValue === '') {
          continue;
        }

        const parsedMaxAmount = Number(trimmedMaxAmountValue);
        if (Number.isNaN(parsedMaxAmount) || parsedMaxAmount < 1) {
          enqueueSnackbar('Commission value limit must be a number greater or equal to 1', { variant: 'error' });
          return;
        }

        commissionValueLimits.push({ region, maxAmount: parsedMaxAmount });
      }
    }

    const additionalInfo = mergeCommissionAdditionalInfo(
      isEdit ? readCommissionAdditionalInfo(data) : undefined,
      conditions.departure,
      conditions.arrival,
    );

    const formObj = {
      rules: buildRuleParam(conditions),
      description: formData.get('description') as string,
      startDate: startDate ? formatDateLongISO(startDate.toDate()) : formatDateLongISO(new Date()),
      endDate: endDate ? formatDateLongISO(endDate.toDate()) : undefined,
      regions: regionsValue,
      discountType: commissionTypeChoice,
      discountAmount: parseInt(formData.get('discountAmount') as string, 10),
      commissionType: commissionTypeChoice,
      commissionPercentage: parseInt(formData.get('discountAmount') as string, 10),
      type: commissionTypeChoice,
      limit: shouldIncludeLimit ? parseInt(limitValue, 10) : null,
      commissionValueLimits,
      ...(additionalInfo ? { additionalInfo } : {}),
    };

    const payload = commissionTypeChoice === 'base' ? { ...formObj, priority, canHaveAdditionalCommissions } : formObj;

    const onSuccess = () => {
      const hasChangedLimit =
        (data?.limit && Number(limitValue) !== data?.limit) || (!data?.limit && Number(limitValue) !== 0);
      if (hasChangedLimit) {
        enqueueSnackbar('Limit updated, this may take a few minutes to take effect', { variant: 'warning' });
      }
      resetForm();
      onClose();
    };

    if (isCreate) {
      createCommissionMutation.mutate(payload as CreateCommissionParams, { onSuccess });
    } else {
      updateCommissionMutation.mutate({ commissionId: data?.id, data: payload as EditCommissionParams }, { onSuccess });
    }
  }

  function onCloseModal() {
    resetForm();
    onClose();
  }

  const isSaving = createCommissionMutation.isPending || updateCommissionMutation.isPending;

  return (
    <Dialog open={isOpen} fullWidth onClose={onCloseModal}>
      <DialogTitle>{isCreate ? 'Create commission rule' : `Edit commission rule "${data?.description}"`}</DialogTitle>
      <form ref={commissionFormRef} onSubmit={(event) => handleSaveCommission(event, data?.id, createdConditions)}>
        <Stack padding={2} paddingTop={0} direction="column" spacing={2}>
          {data?.hasReachedLimit && (
            <Alert severity="warning">
              This commission rule has reached its limit of usage and is no longer active. If you change it, it can
              accidentally become active again.
            </Alert>
          )}
          <Stack direction="row" gap={2}>
            <TextField
              placeholder="e.g. Flight Center UK - Hotel + Tours"
              name="description"
              defaultValue={data?.description}
              label="Description"
              required
              fullWidth
              InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
              InputProps={INPUT_BORDER}
            />
          </Stack>
          <Stack direction="row" gap={2} justifyContent="space-between">
            <FormControl fullWidth>
              <InputLabel
                sx={{ backgroundColor: 'white', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-discount-type-input-label"
              >
                Commission type
              </InputLabel>
              <Select
                value={commissionTypeChoice}
                displayEmpty
                onChange={(event) => setCommissionTypeChoice(event.target.value as CommissionType)}
                renderValue={(value) => {
                  if (!value) {
                    return <Typography color="#7E8188BB">Select commission type</Typography>;
                  }
                  return capitalise(value);
                }}
                label="Commission type"
                required
                fullWidth
                sx={INPUT_BORDER.sx}
              >
                <MenuItem value="base">Base</MenuItem>
                <MenuItem value="additional">Additional</MenuItem>
                <MenuItem value="incentive">Incentive</MenuItem>
              </Select>
            </FormControl>
            <TextField
              name="discountAmount"
              label="Commission percentage"
              defaultValue={data?.commissionPercentage}
              type="number"
              required
              fullWidth
              placeholder="e.g. 8%"
              slotProps={{
                htmlInput: { min: 1, max: 100 },
                input: INPUT_BORDER,
                inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
              }}
            />
          </Stack>

          {commissionTypeChoice === 'base' && (
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="button" color="secondary">
                  Base commission settings
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  These options only apply when the commission type is base.
                </Typography>
              </Box>
              <Stack spacing={1}>
                <TextField
                  value={priorityInput}
                  onChange={(event) => setPriorityInput(event.target.value)}
                  InputProps={{ inputProps: { min: 0, step: 1 }, ...INPUT_BORDER }}
                  label="Priority"
                  type="number"
                  fullWidth
                  placeholder="e.g. 0 (highest)"
                  InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
                />
                <Stack direction="row" justifyContent="flex-end">
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={canHaveAdditionalCommissions}
                        onChange={(event) => setCanHaveAdditionalCommissions(event.target.checked)}
                        size="small"
                      />
                    }
                    label="Can apply additional commissions"
                  />
                </Stack>
              </Stack>
            </Stack>
          )}

          <Stack direction="row" gap={2}>
            <Stack flex={1}>
              <DatePicker
                value={startDate}
                onChange={handleStartDateChange}
                format="DD/MM/YYYY"
                name="startDate"
                label="Start date"
                sx={INPUT_BORDER.sx}
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
            <Stack flex={1}>
              <DatePicker
                value={endDate}
                onChange={handleEndDateChange}
                format="DD/MM/YYYY"
                disablePast
                name="endDate"
                label="End date"
                slotProps={{ textField: { ...SHRUNK_LABEL.textField, InputProps: INPUT_BORDER } }}
              />
            </Stack>
          </Stack>
          <Stack direction="row" gap={2}>
            <FormControl sx={{ flex: 1 }}>
              <InputLabel
                sx={{ backgroundColor: 'white', padding: '0 4px' }}
                shrink
                id="agent-hub-commissions-regions-input-label"
              >
                Regions
              </InputLabel>
              <Select
                displayEmpty
                name="regions"
                labelId="agent-hub-commissions-regions-input-label"
                multiple
                value={regions}
                input={<OutlinedInput label="Regions" />}
                sx={INPUT_BORDER.sx}
                renderValue={(selected: Array<string>) => {
                  if (selected.length === 0) {
                    return <Typography color="#7E8188BB">Select regions</Typography>;
                  }
                  return selected.join(', ');
                }}
                onChange={(event) => {
                  setRegions(event.target.value as Array<string>);
                }}
              >
                {AVAILABLE_REGIONS_LIB_REGIONS.map((region) => (
                  <MenuItem key={region.code} value={region.code}>
                    <Checkbox checked={regions.includes(region.code)} />
                    <ListItemText primary={region.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Stack flex={1} spacing={1}>
              <TextField
                name="limit"
                InputProps={{ inputProps: { min: 0 }, ...INPUT_BORDER }}
                label="Limit (of usage)"
                defaultValue={data?.limit}
                type="number"
                fullWidth
                disabled={!shouldIncludeLimit}
                InputLabelProps={SHRUNK_LABEL.textField.InputLabelProps}
                placeholder="e.g. 5"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldIncludeLimit}
                    onChange={(e) => setShouldIncludeLimit(e.target.checked)}
                    size="small"
                  />
                }
                label="Set limit to commission"
              />
            </Stack>
          </Stack>
          {commissionTypeChoice === 'incentive' && regions.length > 0 && (
            <Stack direction="row" gap={2}>
              {regions.map((region) => (
                <TextField
                  key={region}
                  name={getCommissionLimitInputName(region)}
                  label={`Max amount (${region})`}
                  defaultValue={data?.commissionValueLimits?.find((limit) => limit.region === region)?.maxAmount}
                  type="number"
                  fullWidth
                  placeholder="e.g. 100"
                  slotProps={{
                    htmlInput: { min: 1 },
                    input: INPUT_BORDER,
                    inputLabel: SHRUNK_LABEL.textField.InputLabelProps,
                  }}
                />
              ))}
            </Stack>
          )}
          <Divider />

          <Stack spacing={2}>
            <Box>
              <Typography variant="button" color="secondary">
                Conditions:
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                User tags: require the user account to have at least one of the listed tag identifiers (for example{' '}
                <strong>no_booking_last_6_months</strong>). Add a &quot;User tags&quot; condition to match on tags.
              </Typography>
            </Box>

            {isLoadingConditions && <Spinner size={14} />}
            <Stack direction="column" spacing={3}>
              {COMMISSION_RULE_CONDITION_TYPES.filter((key) => visibleConditions.has(key)).map((key) => {
                const value = createdConditions[key];
                return (
                  <Fragment key={key}>
                    <Stack justifyContent="space-between" key={key} direction="row" spacing={2}>
                      <Box flex={1}>
                        <AddNewConditionForm
                          conditionType={key}
                          ruleConditions={value}
                          addCondition={addCondition}
                          visibleConditions={visibleConditions}
                          handleChangeCondition={handleChangeCondition}
                          {...(key === 'flightCarrier'
                            ? {
                                brand,
                                onFlightCarrierChange: handleFlightCarrierChange,
                              }
                            : {})}
                        />
                      </Box>
                      <DeleteIcon onClick={() => handleClearCondition(key)} />
                    </Stack>
                    <Divider />
                  </Fragment>
                );
              })}
            </Stack>
          </Stack>

          {totalConditions < COMMISSION_RULE_CONDITION_TYPES.length && (
            <Stack direction="row" justifyContent="flex-start" spacing={2}>
              <Button
                size="small"
                variant="text"
                sx={textTransformNone}
                onClick={() => {
                  handleAppendCondition(remainingConditions[0]);
                }}
              >
                <Add /> Add condition
              </Button>
            </Stack>
          )}

          {isLoadingCommissionEditHistory && <Spinner size={14} />}

          {creator && (
            <Stack>
              <Typography variant="caption" color="secondary">
                Created By: <Link href={`/users/${creator?.user.href}`}>{creator?.user.label}</Link> at{' '}
                {formatDateSlashes(data?.createdAt)}
              </Typography>
              {updater && (
                <Typography variant="caption" color="secondary">
                  Edited By: <Link href={`/users/${updater?.user.href}`}>{updater?.user.label}</Link> at{' '}
                  {formatDateSlashes(data?.updatedAt)}
                </Typography>
              )}
            </Stack>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Button sx={textTransformNone} type="button" size="medium" variant="text" onClick={onCloseModal}>
              Cancel
            </Button>
            <Button sx={textTransformNone} type="submit" size="medium" variant="contained" disabled={isSaving}>
              {isSaving ? 'Saving...' : isCreate ? 'Create rule' : 'Save changes'}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Dialog>
  );
}
